'use no memo';

import {Helmet} from '@dr.pogodin/react-helmet';
import {parseISO} from 'date-fns';
import {Action, Card, DataStatus, Form, showNotification} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {indexBy, isNil, reject} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetCorrectiveTaxDocumentV5Query,
  useGetInvoicingDocumentPaymentListQuery,
  useSendCorrectiveTaxDocumentMutation,
} from '@dms/api/finance';
import {PaymentInfoResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, useCurrencies, useGetAccountingDocuments} from '@dms/shared';

import {buildArray, getApiDateString, useRequiredParams} from 'shared/web';

import {CustomerAndSupplierDetail} from '../../components/CustomerAndSupplierDetail';
import {InvoiceDetailHeader} from '../../components/InvoiceDetailHeader';
import {InvoiceDetailSubHeader} from '../../components/InvoiceDetailSubHeader';
import {ListOfItemsDetail} from '../../components/ListOfItemsDetail';
import {PaymentDatesDetail} from '../../components/PaymentDatesDetail';
import {PaymentDetailsDetail} from '../../components/PaymentDetailsDetail';
import {PaymentsListDetail} from '../../components/PaymentsListDetail';
import {PaymentsNotesDetail} from '../../components/PaymentsNotesDetail';
import {SectionsWithCenteredContent} from '../../components/SectionsWithCenteredContent';
import {invoicePaymentOptions} from '../../constants';
import {InvoiceVatRateProvider} from '../../context/InvoiceVatRateProvider';
import {useDocumentActions} from '../../hooks/useDocumentActions';
import {AccountingDocumentFormValues} from '../../types';
import {getCorrectiveTaxDocumentSubFlags} from '../../utils/getCorrectiveTaxDocumentSubFlags';
import {getInvoiceCategoryFlagProps} from '../../utils/getInvoiceCategoryFlagProps';
import {getInvoiceStateFlagProps} from '../../utils/getInvoiceStateFlagProps';
import {getPaymentStateFlagProps} from '../../utils/getPaymentStateFlagProps';
import {getShouldDisplaySendButton} from '../../utils/getShouldDisplaySendButton';
import {CorrectiveTaxDocumentDetails} from './components/CorrectiveTaxDocumentDetails';

export function CorrectiveTaxDocumentDetail() {
  const {id: correctiveTaxDocumentId} = useRequiredParams();

  const {getCurrencyName} = useCurrencies();
  const [subheaderActions] = useDocumentActions('CORRECTIVE_TAX_DOCUMENT', correctiveTaxDocumentId);

  const [sendInvoice, {isLoading: isSendingDocument}] = useSendCorrectiveTaxDocumentMutation();

  const {
    data: correctiveTaxDocument,
    isLoading: isLoadingCorrectiveTaxDocument,
    isError: isCorrectiveTaxDocumentError,
  } = useGetCorrectiveTaxDocumentV5Query({correctiveTaxDocumentId});
  const {data: correctedDocument} = useGetAccountingDocuments(
    correctiveTaxDocument?.invoicingDocumentId,
    correctiveTaxDocument?.invoicingDocumentType
  );

  const {
    data: paymentList,
    isLoading: isLoadingPaymentList,
    isError: isPaymentListError,
  } = useGetInvoicingDocumentPaymentListQuery({
    invoicingDocumentType: 'corrective_tax_document',
    invoicingDocumentId: correctiveTaxDocumentId,
  });

  const isLoading = isLoadingCorrectiveTaxDocument || isLoadingPaymentList;
  const isError = isCorrectiveTaxDocumentError || isPaymentListError;

  const bankAccountValue = isNotNilOrEmpty(correctiveTaxDocument?.paymentInfo?.bankAccount)
    ? reject(isNil, [
        correctiveTaxDocument?.paymentInfo?.bankAccount,
        correctiveTaxDocument?.paymentInfo?.bankName,
        correctiveTaxDocument?.currency,
      ]).join(', ')
    : null;

  const shouldDisplaySendButton = getShouldDisplaySendButton(correctiveTaxDocument);
  const paymentTypesByType = indexBy((item) => item.value, invoicePaymentOptions);
  const paymentMethodLabel = correctiveTaxDocument
    ? (paymentTypesByType[correctiveTaxDocument.paymentInfo.paymentMethod]
        .label as PaymentInfoResponseBody['paymentMethod'])
    : 'BANK_TRANSFER';

  const initialValues = correctiveTaxDocument
    ? {
        ...correctiveTaxDocument,
        paymentInfo: {
          ...correctiveTaxDocument.paymentInfo,
          bankAccount: bankAccountValue,
          paymentMethod: paymentMethodLabel,
        },
        footerNote: correctiveTaxDocument.behindItemsNote,
        exchangeRateRatio: {
          ...correctiveTaxDocument.exchangeRateRatio,
          amount: correctiveTaxDocument.exchangeRateRatio?.amount?.toString() ?? null,
          currency: getCurrencyName(correctiveTaxDocument.exchangeRateRatio?.currency),
          type: i18n.t('general.actions.recalculate'),
        },
      }
    : undefined;

  const handleSendInvoice = () => {
    if (isNil(correctiveTaxDocument)) {
      showNotification.error();
      return;
    }

    sendInvoice({correctiveTaxDocumentId: correctiveTaxDocument.id}).unwrap().catch(handleApiError);
  };

  const invoiceSubActions = buildArray<Action>().when(shouldDisplaySendButton, {
    type: 'button',
    title: i18n.t('general.actions.send'),
    onClick: handleSendInvoice,
    isLoading: isSendingDocument,
    variant: 'link',
    leftIcon: 'content/send',
  });

  const pageTitle =
    correctiveTaxDocument?.invoiceCategory === 'internal'
      ? i18n.t('entity.correctiveTaxDocument.labels.internalCorrectiveTaxDocumentNumber', {
          documentNumber: correctiveTaxDocument?.number,
        })
      : i18n.t('entity.correctiveTaxDocument.labels.correctiveTaxDocumentNumber', {
          documentNumber: correctiveTaxDocument?.number,
        });

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight="80vh">
      <Show when={isNotNil(correctiveTaxDocument)}>
        <Helmet title={pageTitle} />

        <Form<AccountingDocumentFormValues> defaultValues={initialValues}>
          {(control, formApi) => (
            <InvoiceVatRateProvider
              control={control}
              listItemType="invoicing-documents"
              date={
                correctedDocument?.dateOfTaxableSupply
                  ? getApiDateString(parseISO(correctedDocument?.dateOfTaxableSupply))
                  : undefined
              }
            >
              <VStack>
                <Box backgroundColor="general.white">
                  <InvoiceDetailHeader title={pageTitle} />

                  <InvoiceDetailSubHeader
                    author={correctiveTaxDocument!.author}
                    actions={subheaderActions}
                    subActions={invoiceSubActions}
                    createdAt={correctiveTaxDocument!.createdAt}
                    data-testid={testIds.accounting.correctiveTaxDocumentDetail('subHeader')}
                    flags={reject(isNil, [
                      getInvoiceStateFlagProps(correctiveTaxDocument),
                      getPaymentStateFlagProps(correctiveTaxDocument!.paymentState),
                      getInvoiceCategoryFlagProps(correctiveTaxDocument?.invoiceCategory),
                    ])}
                    subFlags={getCorrectiveTaxDocumentSubFlags(correctiveTaxDocument)}
                  />
                </Box>

                <SectionsWithCenteredContent
                  data-testid="accounting-sections"
                  sections={[
                    {
                      content: (
                        <VStack spacing={4}>
                          <Card>
                            <VStack spacing={4}>
                              <CustomerAndSupplierDetail
                                customer={correctiveTaxDocument!.customer}
                                supplier={correctiveTaxDocument!.supplier}
                                additionalCustomer={correctiveTaxDocument?.additionalCustomer}
                                additionalFields={correctiveTaxDocument?.customFields}
                                data-testid={testIds.accounting.correctiveTaxDocumentDetail(
                                  'customer-and-supplier'
                                )}
                              />
                              <PaymentDatesDetail control={control} />
                            </VStack>
                          </Card>

                          <CorrectiveTaxDocumentDetails
                            control={control}
                            formApi={formApi}
                            correctiveTaxDocument={correctiveTaxDocument}
                            data-testid={testIds.accounting.correctiveTaxDocumentDetail(
                              'CorrectiveTaxDocumentDetails'
                            )}
                          />

                          <PaymentDetailsDetail control={control} />

                          <ListOfItemsDetail
                            items={correctiveTaxDocument!.items}
                            summary={correctiveTaxDocument!.documentSummary}
                            exchangeRateRatioCalculation={
                              correctiveTaxDocument!.exchangeRateRatioDocumentSummary
                            }
                            control={control}
                          />

                          <PaymentsNotesDetail control={control} />
                        </VStack>
                      ),
                      id: 'detail',
                      label: i18n.t('general.labels.details'),
                    },
                    {
                      content: (
                        <PaymentsListDetail
                          paymentItems={paymentList}
                          invoiceType="invoice"
                          data-testid={testIds.accounting.invoiceDetail('paymentList')}
                        />
                      ),
                      id: 'payments',
                      label: i18n.t('page.accounting.invoiceDetailPayments.title'),
                    },
                  ]}
                />
              </VStack>
            </InvoiceVatRateProvider>
          )}
        </Form>
      </Show>
    </DataStatus>
  );
}
